import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, ExternalLink, LogOut, ThumbsUp, ThumbsDown } from "lucide-react";
import { Toaster, toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  fetchAdminLeads,
  fetchAdminStats,
  getAdminToken,
  setAdminToken,
} from "@/lib/api";

export default function AdminDashboard() {
  const [tokenInput, setTokenInput] = useState("");
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        fetchAdminLeads(),
        fetchAdminStats(),
      ]);
      setData(leadsRes);
      setStats(statsRes);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Load failed.";
      setError(msg);
      if (e?.response?.status === 401) {
        setAdminToken("");
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogin = () => {
    const t = tokenInput.trim();
    if (!t) return;
    setAdminToken(t);
    setAuthed(true);
  };

  const handleLogout = () => {
    setAdminToken("");
    setAuthed(false);
    setData(null);
    setStats(null);
    toast.success("Signed out.");
  };

  return (
    <div className="min-h-screen bg-[#0B0F10] text-white">
      <Toaster theme="dark" position="top-right" />
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B0F10]/70 border-b border-white/5">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[#35D07F]/15 border border-[#35D07F]/40">
              <span className="h-2 w-2 rounded-sm bg-[#35D07F]" />
            </span>
            <div>
              <div className="font-bold tracking-tight text-white leading-none">
                ScopeGrid
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-0.5">
                by Tector · admin
              </div>
            </div>
          </Link>
          {authed && (
            <div className="flex items-center gap-3">
              <button
                onClick={load}
                data-testid="admin-refresh"
                disabled={loading}
                className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white disabled:opacity-40"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
              <button
                onClick={handleLogout}
                data-testid="admin-logout"
                className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
              >
                <LogOut className="h-3.5 w-3.5" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 sm:px-8 py-10 sm:py-14">
        {!authed && (
          <div
            className="max-w-md mx-auto rounded-2xl border border-white/10 bg-[#12181A] p-8"
            data-testid="admin-login"
          >
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
              Admin sign-in
            </div>
            <h1 className="mt-2 text-2xl font-bold text-white">
              Enter your admin token
            </h1>
            <p className="mt-2 text-sm text-[#A0AAB2]">
              Ask ops for the token. Stored locally on this device only.
            </p>
            <div className="mt-6 space-y-3">
              <Input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                data-testid="admin-token-input"
                placeholder="Paste admin token"
                className="bg-[#0B0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#35D07F]/50"
              />
              <Button
                onClick={handleLogin}
                data-testid="admin-signin"
                className="w-full rounded-full bg-[#35D07F] hover:bg-[#2CBB70] text-black font-semibold h-11 disabled:opacity-40"
              >
                Sign in
              </Button>
              {error && (
                <div className="text-sm text-[#FF5252]" data-testid="admin-login-error">
                  {error}
                </div>
              )}
            </div>
          </div>
        )}

        {authed && (
          <div className="space-y-8" data-testid="admin-panel">
            <StatCards stats={stats} loading={loading} />
            <FeedbackCards stats={stats} loading={loading} />
            <LeadsTable data={data} loading={loading} error={error} />
          </div>
        )}
      </main>
    </div>
  );
}

function StatCards({ stats, loading }) {
  const cells = [
    { label: "Leads today", value: stats?.today_leads, testid: "stat-today" },
    { label: "Total leads", value: stats?.total_leads, testid: "stat-total-leads" },
    { label: "Total cases", value: stats?.total_cases, testid: "stat-total-cases" },
    { label: "Tector early access", value: stats?.tector_bridge_leads, testid: "stat-tector-bridge" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {cells.map((c) => (
        <div
          key={c.label}
          data-testid={c.testid}
          className="rounded-2xl border border-white/10 bg-[#12181A] p-6"
        >
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
            {c.label}
          </div>
          <div className="mt-2 text-4xl font-black tracking-tight text-white">
            {loading && c.value == null ? (
              <Loader2 className="h-6 w-6 animate-spin text-white/40" />
            ) : c.value == null ? (
              "—"
            ) : (
              c.value
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function FeedbackCards({ stats, loading }) {
  const fb = stats?.feedback || {};
  const rows = [
    {
      label: "Question votes",
      up: fb.question_up,
      down: fb.question_down,
      testid: "fb-question",
    },
    {
      label: "Answer votes",
      up: fb.answer_up,
      down: fb.answer_down,
      testid: "fb-answer",
    },
    {
      label: "Overall votes",
      up: fb.overall_up,
      down: fb.overall_down,
      extra: fb.overall_comments,
      extraLabel: "comments",
      testid: "fb-overall",
    },
  ];
  return (
    <div className="rounded-2xl border border-white/10 bg-[#12181A] p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
            User feedback
          </div>
          <div className="mt-1 text-sm text-white/80">
            Upvotes vs downvotes across the app.
          </div>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-[#35D07F]" />}
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {rows.map((r) => (
          <div
            key={r.label}
            data-testid={r.testid}
            className="rounded-xl border border-white/10 bg-[#0B0F10] p-5"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">
              {r.label}
            </div>
            <div className="mt-3 flex items-end gap-4">
              <div className="flex items-baseline gap-1.5">
                <ThumbsUp className="h-4 w-4 text-[#35D07F]" strokeWidth={2.5} />
                <div
                  className="text-3xl font-black text-white tracking-tight"
                  data-testid={`${r.testid}-up`}
                >
                  {r.up ?? 0}
                </div>
              </div>
              <div className="flex items-baseline gap-1.5">
                <ThumbsDown className="h-4 w-4 text-[#FF5252]" strokeWidth={2.5} />
                <div
                  className="text-3xl font-black text-white tracking-tight"
                  data-testid={`${r.testid}-down`}
                >
                  {r.down ?? 0}
                </div>
              </div>
            </div>
            {r.extra != null && (
              <div
                className="mt-3 text-xs text-white/50"
                data-testid={`${r.testid}-extra`}
              >
                {r.extra} written {r.extraLabel}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadsTable({ data, loading, error }) {
  const leads = data?.leads || [];

  if (error && !loading) {
    return (
      <div className="rounded-2xl border border-[#FF5252]/40 bg-[#FF5252]/[0.06] p-4 text-sm text-[#FFB4B4]">
        {error}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-[#12181A] overflow-hidden">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
            Signups
          </div>
          <div className="mt-1 text-sm text-white/80">
            {leads.length === 0 && !loading
              ? "No signups yet."
              : `${leads.length} lead${leads.length === 1 ? "" : "s"} · most recent first`}
          </div>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-[#35D07F]" />}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-white/40 border-b border-white/5">
              <th className="px-6 py-3 font-medium">When</th>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Problem</th>
              <th className="px-4 py-3 font-medium">Business case</th>
              <th className="px-6 py-3 font-medium">Report</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l, i) => (
              <tr
                key={l.id || i}
                data-testid={`admin-lead-${i}`}
                className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]"
              >
                <td className="px-6 py-4 text-white/70 text-xs whitespace-nowrap align-top">
                  {formatWhen(l.created_at)}
                </td>
                <td className="px-4 py-4 text-white font-medium align-top">
                  {l.name}
                </td>
                <td className="px-4 py-4 text-white/80 align-top">
                  <a
                    href={`mailto:${l.email}`}
                    className="hover:text-[#35D07F] transition-colors"
                  >
                    {l.email}
                  </a>
                </td>
                <td className="px-4 py-4 align-top">
                  <span className="inline-flex rounded-full border border-white/10 bg-white/5 text-white/80 text-[11px] px-2.5 py-0.5">
                    {l.problem_type_display || l.problem_type || "—"}
                  </span>
                </td>
                <td className="px-4 py-4 text-white/80 align-top max-w-md">
                  <div className="line-clamp-4 whitespace-pre-wrap text-[13px] leading-snug">
                    {l.raw_prompt || <span className="text-white/40">—</span>}
                  </div>
                </td>
                <td className="px-6 py-4 align-top whitespace-nowrap">
                  {l.case_id ? (
                    <Link
                      to={`/report/${l.case_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#35D07F] hover:underline text-xs"
                    >
                      <ExternalLink className="h-3 w-3" strokeWidth={2} />
                      Open
                    </Link>
                  ) : (
                    <span className="text-white/40 text-xs">—</span>
                  )}
                </td>
              </tr>
            ))}
            {!loading && leads.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-white/40 text-sm">
                  Nothing here yet. Signups will appear as they come in.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
      " · " +
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return iso;
  }
}
